/* eslint-disable react-refresh/only-export-components */
/**
 * @fileoverview Snooze picker for inbox notifications (MOR-716).
 *
 * Dropdown with the three presets (later today +3h, tomorrow 9am, next week
 * Monday 9am) plus a custom date-time dialog. Emits the chosen resurface
 * time as an epoch-ms timestamp.
 */

import { useState, type ReactNode } from "react";
import { format } from "date-fns";
import { Clock } from "lucide-react";
import { Button } from "@/ui/primitives/button";
import { Label } from "@/ui/primitives/label";
import { DatePicker } from "@/ui/composed/inputs/date-picker";
import { TimePicker } from "@/ui/composed/inputs/time-picker";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/ui/composed/menus/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/ui/composed/overlays/dialog";

export function snoozePresets(now: Date): { label: string; until: number }[] {
  const later = new Date(now);
  later.setHours(now.getHours() + 3);
  const tomorrow = new Date(now);
  tomorrow.setDate(now.getDate() + 1);
  tomorrow.setHours(9, 0, 0, 0);
  const nextWeek = new Date(now);
  nextWeek.setDate(now.getDate() + ((8 - now.getDay()) % 7 || 7));
  nextWeek.setHours(9, 0, 0, 0);
  return [
    { label: "Later today", until: later.getTime() },
    { label: "Tomorrow morning", until: tomorrow.getTime() },
    { label: "Next week", until: nextWeek.getTime() },
  ];
}

export function SnoozeMenu({
  onSnooze,
  trigger,
}: {
  onSnooze: (until: number) => void;
  /** Optional custom trigger (defaults to the small clock icon button). */
  trigger?: ReactNode;
}) {
  const [customOpen, setCustomOpen] = useState(false);
  const [customDate, setCustomDate] = useState("");
  const [customTime, setCustomTime] = useState("09:00");

  const customUntil =
    customDate && customTime
      ? new Date(`${customDate}T${customTime}`).getTime()
      : null;

  const openCustom = () => {
    // Default to tomorrow morning; both fields stay editable.
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    setCustomDate(format(tomorrow, "yyyy-MM-dd"));
    setCustomTime("09:00");
    setCustomOpen(true);
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          {trigger ?? (
            <Button
              variant="ghost"
              size="icon"
              className="size-7"
              aria-label="Snooze"
            >
              <Clock className="size-3.5" />
            </Button>
          )}
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {snoozePresets(new Date()).map((p) => (
            <DropdownMenuItem key={p.label} onSelect={() => onSnooze(p.until)}>
              {p.label}
            </DropdownMenuItem>
          ))}
          <DropdownMenuSeparator />
          <DropdownMenuItem onSelect={openCustom}>Custom…</DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={customOpen} onOpenChange={setCustomOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Snooze until</DialogTitle>
          </DialogHeader>
          <div className="flex gap-3">
            <div className="flex-1 space-y-1.5">
              <Label htmlFor="snooze-date">Date</Label>
              <DatePicker
                value={customDate}
                onChange={setCustomDate}
                placeholder="Pick a date"
              />
            </div>
            <div className="w-36 space-y-1.5">
              <Label htmlFor="snooze-time">Time</Label>
              <TimePicker value={customTime} onChange={setCustomTime} />
            </div>
          </div>
          {customUntil !== null && customUntil <= Date.now() && (
            <p className="text-xs text-destructive">
              Pick a time in the future.
            </p>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setCustomOpen(false)}>
              Cancel
            </Button>
            <Button
              disabled={customUntil === null || customUntil <= Date.now()}
              onClick={() => {
                if (customUntil === null) return;
                onSnooze(customUntil);
                setCustomOpen(false);
              }}
            >
              Snooze
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
